import { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import { Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudyBuddySubscribe() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user]);

  // Check for payment completion on page load
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && user) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, user]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please contact support.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/subscriptions/checkout-status/${sessionId}`,
        { withCredentials: true }
      );

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Welcome to Premium! 🎉');
        setCheckingPayment(false);
        // Redirect to Study Buddy main page after a short delay
        setTimeout(() => {
          navigate('/study-buddy');
        }, 2000);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // If payment is still pending, continue polling
      toast.info('Payment is being processed...');
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status. Please try again.');
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      // Get current origin for success/cancel URLs
      const originUrl = window.location.origin;

      const response = await axios.post(
        `${API_URL}/api/subscriptions/create-checkout?origin_url=${encodeURIComponent(originUrl)}`,
        {},
        { withCredentials: true }
      );

      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start subscription. Please try again.');
      setLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <div className="min-h-screen py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold retro-title text-[#2C1810] mb-4">
              Unlock Your Full Potential
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Upgrade to Premium and manage all your college courses with AI-powered study planning
            </p>
          </div>

          {/* Pricing Comparison */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Free Tier */}
            <div className="cassette-card p-8 relative">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-[#2C1810] mb-2">Free Trial</h3>
                <div className="text-4xl font-bold text-[#E85D4D] mb-2">$0</div>
                <p className="text-gray-600">Forever</p>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">1 college course</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AI syllabus analysis</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Smart calendar tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">S.M.A.R.T. Goals creation</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">Multiple courses</span>
                </li>
                <li className="flex items-start gap-3">
                  <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-400">Central dashboard</span>
                </li>
              </ul>

              <button
                disabled
                className="w-full px-6 py-3 bg-gray-400 text-white font-bold rounded-lg cursor-not-allowed"
              >
                Current Plan
              </button>
            </div>

            {/* Premium Tier */}
            <div className="cassette-card p-8 relative border-4 border-[#E8934D] shadow-2xl">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#E8934D] text-white px-4 py-1 rounded-full text-sm font-bold">
                  RECOMMENDED
                </span>
              </div>

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-[#2C1810] mb-2">Premium</h3>
                <div className="text-4xl font-bold text-[#E8934D] mb-2">$10</div>
                <p className="text-gray-600">per month</p>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-bold">Up to 10 college courses</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">AI syllabus analysis</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Smart calendar tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Unlimited S.M.A.R.T. Goals</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-bold">Central "My Courses" dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 font-bold">Priority support</span>
                </li>
              </ul>

              <button
                onClick={handleSubscribe}
                disabled={loading || checkingPayment}
                className="w-full px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
              >
                {checkingPayment ? 'Verifying Payment...' : loading ? 'Redirecting...' : 'Unlock All 10 Courses'}
              </button>
            </div>
          </div>

          {/* Additional Benefits */}
          <div className="cassette-card p-8">
            <h3 className="text-2xl font-bold text-[#2C1810] mb-6 text-center">
              Premium Benefits
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl mb-3">📚</div>
                <h4 className="font-bold text-[#2C1810] mb-2">Multiple Courses</h4>
                <p className="text-sm text-gray-600">Manage up to 10 college courses simultaneously</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🤖</div>
                <h4 className="font-bold text-[#2C1810] mb-2">AI-Powered</h4>
                <p className="text-sm text-gray-600">Upload syllabi and get instant AI analysis</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">📅</div>
                <h4 className="font-bold text-[#2C1810] mb-2">Smart Scheduling</h4>
                <p className="text-sm text-gray-600">Track all deadlines in one unified calendar</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h4 className="font-bold text-[#2C1810] mb-2">S.M.A.R.T. Goals</h4>
                <p className="text-sm text-gray-600">Create personalized study plans for every assignment</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">📊</div>
                <h4 className="font-bold text-[#2C1810] mb-2">Central Dashboard</h4>
                <p className="text-sm text-gray-600">View all courses and deadlines in one place</p>
              </div>
              <div className="text-center">
                <div className="text-4xl mb-3">⚡</div>
                <h4 className="font-bold text-[#2C1810] mb-2">Priority Support</h4>
                <p className="text-sm text-gray-600">Get help when you need it most</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/study-buddy')}
              className="text-[#2C1810] hover:text-[#E85D4D] font-semibold underline"
            >
              ← Back to Study Buddy
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
